import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { ContentWithConfig, ContentConfig, AdminRole } from '@/types/custom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  cn,
  fixDropboxUrl,
  normalizeImageUrl,
  formatTime,
  parseTranscription,
  sortObjectsByField,
  TranscriptionLine,
  retryImageOnce,
} from '@/lib/utils';
import { SkipBack, SkipForward, Play, Pause, Volume2, Maximize2, Minimize2, X, Heart } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useOfflineSrc } from '@/lib/hooks/use-offline-src';
import { OfflineDownloadButton } from '@/components/meditation/offline-download-button';
import { useFavorites } from '@/lib/hooks/use-favorites';
import { ScrollArea } from '@/components/ui/scroll-area';
import { GoBackIcon } from '../ui/icons/go-back';
import FavoriteButton from '../favorite-button';
import { ROUTES } from '@/lib/constants';
import { trackPlaytime } from '@/lib/activity';
import { useProfile } from '@/lib/providers';

interface MeditationSelectionProps {
  contentData: ContentWithConfig;
  onNext?: () => void;
  selectionContent?: React.ReactNode;
  fullscreenContent?: React.ReactNode;
  hideArtwork?: boolean;
  autoShowTranscript?: boolean;
  onBack?: () => void;
  onTimeUpdate?: (timeSec: number) => void;
  onPlayStateChange?: (playing: boolean) => void;
}

/**
 * Select and play a meditation from available configurations.
 * E.g. the voices available, disabling music, etc.
 * TODO: merge with audio-player.tsx?
 */
export default function MeditationPlayer({
  contentData,
  onNext,
  selectionContent,
  fullscreenContent,
  hideArtwork,
  autoShowTranscript,
  onBack,
  onTimeUpdate: onTimeUpdateCb,
  onPlayStateChange: onPlayStateChangeCb,
}: MeditationSelectionProps) {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const isAdmin = profile?.role === AdminRole;

  // Hide menu/profile button when player is active
  useEffect(() => {
    document.documentElement.classList.add('player-active');
    return () => document.documentElement.classList.remove('player-active');
  }, []);
  const { favorites, toggleFavorite } = useFavorites();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [voiceConfigIdx, setVoiceConfigIdx] = useState<number>(0); // Which voice is selected (Leo/Dana)
  const [voiceAudioEnabled, setVoiceAudioEnabled] = useState(true); // Whether to play voice audio
  const [musicConfigIdx, setMusicConfigIdx] = useState(-1);

  const availableVoiceConfigs = contentData.configs
    .filter((c) => c.voice)
    .sort((a, b) => sortObjectsByField(a, b, 'voice'));
  const availableMusicConfigs = contentData.configs.filter((c) => !c.voice);

  const [initDefaults, setInitDefaults] = useState(true); // whether we need to select default configs (on load)
  const [trackPlayDur] = trackPlaytime(
    contentData.content.id,
    profile?.id ?? null,
  );

  useEffect(() => {
    if (
      initDefaults &&
      availableMusicConfigs.length > 0 &&
      musicConfigIdx === -1
    ) {
      setInitDefaults(false);
      setMusicConfigIdx(0); // default to first music config (should only be 1)
    }
  }, [availableMusicConfigs.length, musicConfigIdx, initDefaults]);

  useEffect(() => {
    if (initDefaults && availableVoiceConfigs.length > 0) {
      setInitDefaults(false);
      setVoiceConfigIdx(0); // default to first voice config
    }
  }, [availableVoiceConfigs.length, initDefaults]);

  // Selected voice config (for transcription) - always set if voices exist
  const selectedVoiceConfig: ContentConfig | undefined =
    availableVoiceConfigs.length > 0 ? availableVoiceConfigs[voiceConfigIdx] : undefined;
  const selectedMusicConfig: ContentConfig | undefined =
    musicConfigIdx < 0 ? undefined : availableMusicConfigs[musicConfigIdx];
  const musicEnabled = musicConfigIdx != -1;
  const voiceEnabled = voiceAudioEnabled && availableVoiceConfigs.length > 0;

  const [selectionMode, setSelectionMode] = useState(true); // whether to show selection or play control
  const [isPlaying, setIsPlaying] = useState(false);
  const [curTimeSec, setCurTimeSec] = useState(0);
  const [playDuration, setPlayDuration] = useState(0);
  const isIOS = (window as any).Capacitor?.getPlatform?.() === 'ios';
  const [volume, setVolume] = useState(1);
  const [showTranscript, setShowTranscript] = useState(false);
  const [showMobileVolume, setShowMobileVolume] = useState(false);
  const mobileVolumeRef = useRef<HTMLDivElement>(null);
  const [transcription, setTranscription] = useState<string>('');
  const [transcriptionLoading, setTranscriptionLoading] = useState(false);

  // Bumped after a download or a delete so the sources re-resolve.
  const [offlineKey, setOfflineKey] = useState(0);
  const musicAudioRef = useRef<HTMLAudioElement>(null);
  const voiceAudioRef = useRef<HTMLAudioElement>(null);
  const musicSrc = useOfflineSrc(selectedMusicConfig?.audio_link, offlineKey);
  const voiceSrc = useOfflineSrc(selectedVoiceConfig?.audio_link, offlineKey);
  const transcriptionRef = useRef<HTMLDivElement>(null);
  const lyricsTrackRef = useRef<HTMLDivElement>(null);
  const fsTranscriptionRef = useRef<HTMLDivElement>(null);
  const fsLyricsTrackRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const simulatedTimeRef = useRef<number>(0);
  const lastFrameTimeRef = useRef<number>(0);

  const transcriptionUrl = selectedVoiceConfig?.transcription_url
    || selectedMusicConfig?.transcription_url
    || contentData.configs.find((c) => c.transcription_url)?.transcription_url;

  // Fire callbacks for external consumers (e.g. exercise overlay)
  useEffect(() => {
    onTimeUpdateCb?.(curTimeSec);
  }, [curTimeSec, onTimeUpdateCb]);

  useEffect(() => {
    onPlayStateChangeCb?.(isPlaying);
  }, [isPlaying, onPlayStateChangeCb]);

  // Close mobile volume popup when clicking outside
  useEffect(() => {
    if (!showMobileVolume) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (mobileVolumeRef.current && !mobileVolumeRef.current.contains(e.target as Node)) {
        setShowMobileVolume(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMobileVolume]);

  // Fetch transcription from URL when config changes
  const fetchTranscription = useCallback(async (url: string) => {
    setTranscriptionLoading(true);
    // Fix Dropbox URLs and ensure protocol prefix
    let fullUrl = fixDropboxUrl(url);
    if (!fullUrl.startsWith('http')) fullUrl = `https://${fullUrl}`;
    try {
      const response = await fetch(fullUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch transcription: ${response.status}`);
      }
      const text = await response.text();
      setTranscription(text);
    } catch (error) {
      console.error('Error fetching transcription:', error);
      setTranscription('');
    } finally {
      setTranscriptionLoading(false);
    }
  }, []);

  useEffect(() => {
    if (transcriptionUrl) {
      fetchTranscription(transcriptionUrl);
    } else {
      setTranscription('');
    }
  }, [transcriptionUrl, fetchTranscription]);

  const transcriptAvailable = !!transcription && !transcriptionLoading;

  // Auto-show transcript when prop is set and transcription becomes available
  useEffect(() => {
    if (autoShowTranscript && transcriptAvailable) {
      setShowTranscript(true);
    }
  }, [autoShowTranscript, transcriptAvailable]);

  const parsedTranscription = useMemo(
    () => parseTranscription(transcription),
    [transcription],
  );


  // Set duration from transcription when in transcription-only mode
  useEffect(() => {
    const hasAudio = selectedMusicConfig || selectedVoiceConfig;
    if (!hasAudio && parsedTranscription.length > 0) {
      const lastLine = parsedTranscription[parsedTranscription.length - 1];
      setPlayDuration(lastLine.endTime);
    }
  }, [parsedTranscription, selectedMusicConfig, selectedVoiceConfig]);

  // Track play duration for analytics
  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => {
      if (musicAudioRef.current || voiceAudioRef.current || showTranscript) {
        trackPlayDur((prev) => prev + 1);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isPlaying, trackPlayDur, showTranscript]);

  useEffect(() => {
    // only admins can preview the player for unpublished content
    if (!contentData.content.published && !isAdmin) {
      navigate(ROUTES.NOT_FOUND, { replace: true });
    }
  }, [contentData.content.published, isAdmin, navigate]);

  // High-precision sync loop using requestAnimationFrame
  useEffect(() => {
    if (!isPlaying || !showTranscript || parsedTranscription.length === 0) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      // Reset simulated time when stopping
      if (!isPlaying) {
        simulatedTimeRef.current = 0;
        lastFrameTimeRef.current = 0;
      }
      return;
    }

    const syncLoop = (timestamp: number) => {
      const audio = voiceAudioRef.current || musicAudioRef.current;

      let currentTime: number;

      if (audio) {
        // Use audio time when available
        currentTime = audio.currentTime + 0.15;
      } else {
        // Transcription-only mode: simulate time progression
        if (lastFrameTimeRef.current === 0) {
          lastFrameTimeRef.current = timestamp;
        }
        const deltaTime = (timestamp - lastFrameTimeRef.current) / 1000;
        lastFrameTimeRef.current = timestamp;
        simulatedTimeRef.current += deltaTime;
        currentTime = simulatedTimeRef.current;

        // Update the displayed time
        setCurTimeSec(currentTime);
      }
      const track = isFullscreen ? fsLyricsTrackRef.current : lyricsTrackRef.current;
      const container = isFullscreen ? fsTranscriptionRef.current : transcriptionRef.current;

      if (!track || !container) {
        animationFrameRef.current = requestAnimationFrame((ts) => syncLoop(ts));
        return;
      }

      let activeLineIdx = -1;

      // Update line and word highlighting
      parsedTranscription.forEach((line, lIdx) => {
        const lineElem = document.getElementById(`lyric-line-${lIdx}`);
        if (!lineElem) return;

        const isActiveLine =
          currentTime >= line.startTime && currentTime <= line.endTime + 0.2;

        if (isActiveLine) {
          activeLineIdx = lIdx;
          lineElem.classList.add('active');

          // Word-level highlighting
          if (line.words && line.words.length > 0) {
            line.words.forEach((word, wIdx) => {
              const wordElem = document.getElementById(`word-${lIdx}-${wIdx}`);
              if (wordElem) {
                if (currentTime >= word.startTime) {
                  wordElem.classList.add('highlighted');
                } else {
                  wordElem.classList.remove('highlighted');
                }
              }
            });
          }
        } else {
          lineElem.classList.remove('active');
        }
      });

      // Smooth scroll to active line using transform - center it vertically
      if (activeLineIdx !== -1) {
        const activeLineElem = document.getElementById(`lyric-line-${activeLineIdx}`);
        if (activeLineElem) {
          const containerHeight = container.clientHeight;
          const lineTop = activeLineElem.offsetTop;
          const lineHeight = activeLineElem.clientHeight;
          // Center the active line in the container
          const offset = lineTop - (containerHeight / 2) + (lineHeight / 2);
          track.style.transform = `translateY(${-offset}px)`;
        }
      } else {
        // Reset to initial position when no active line
        track.style.transform = `translateY(0px)`;
      }

      // Check if transcription-only mode has ended
      const hasAudio = voiceAudioRef.current || musicAudioRef.current;
      if (!hasAudio && parsedTranscription.length > 0) {
        const lastLine = parsedTranscription[parsedTranscription.length - 1];
        if (currentTime > lastLine.endTime + 1) {
          setIsPlaying(false);
          return;
        }
      }

      animationFrameRef.current = requestAnimationFrame((ts) => syncLoop(ts));
    };

    animationFrameRef.current = requestAnimationFrame((ts) => syncLoop(ts));

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, showTranscript, parsedTranscription, isFullscreen]);

  useEffect(() => {
    const playAudio = async () => {
      if (musicAudioRef.current || voiceAudioRef.current) {
        try {
          const currentTime =
            musicAudioRef.current?.currentTime ||
            voiceAudioRef.current?.currentTime ||
            0;

          // Sync the current time between tracks if both are available
          if (musicAudioRef.current && voiceAudioRef.current) {
            musicAudioRef.current.currentTime = currentTime;
            voiceAudioRef.current.currentTime = currentTime;
          }

          if (isPlaying) {
            const playPromises = [];
            if (musicEnabled && musicAudioRef.current) {
              playPromises.push(musicAudioRef.current.play());
            } else if (musicAudioRef.current) {
              musicAudioRef.current.pause();
            }

            if (voiceEnabled && voiceAudioRef.current) {
              playPromises.push(voiceAudioRef.current.play());
            } else if (voiceAudioRef.current) {
              voiceAudioRef.current.pause();
            }
            await Promise.all(playPromises);
            return;
          }

          if (musicAudioRef.current) musicAudioRef.current.pause();
          if (voiceAudioRef.current) voiceAudioRef.current.pause();
        } catch (error) {
          console.error('Error playing audio:', error);
        }
      }
    };
    playAudio();
  }, [isPlaying, musicEnabled, voiceEnabled]);

  // on voice config change
  useEffect(() => {
    if (!voiceAudioRef.current || !selectedVoiceConfig?.audio_link) return;

    const currentTime = voiceAudioRef.current.currentTime;
    const wasPlaying = !voiceAudioRef.current.paused;

    const newAudio = new Audio(fixDropboxUrl(selectedVoiceConfig?.audio_link) || '');
    newAudio.addEventListener(
      'loadedmetadata',
      () => {
        if (voiceAudioRef.current) {
          voiceAudioRef.current.src = newAudio.src;
          voiceAudioRef.current.currentTime = currentTime;
          if (wasPlaying && voiceEnabled) {
            voiceAudioRef.current.play();
          }
        }
      },
      { once: true },
    );
  }, [selectedVoiceConfig?.audio_link]);

  // on music config change
  useEffect(() => {
    if (!musicAudioRef.current || !selectedMusicConfig?.audio_link) return;

    const currentTime = musicAudioRef.current.currentTime;
    const wasPlaying = !musicAudioRef.current.paused;

    const newAudio = new Audio(fixDropboxUrl(selectedMusicConfig?.audio_link) || '');
    newAudio.addEventListener(
      'loadedmetadata',
      () => {
        if (musicAudioRef.current) {
          musicAudioRef.current.src = newAudio.src;
          musicAudioRef.current.currentTime = currentTime;
          if (wasPlaying && musicEnabled) {
            musicAudioRef.current.play();
          }
        }
      },
      { once: true },
    );
  }, [selectedMusicConfig?.audio_link]);

  const handleTimeUpdate = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    const newTime = e.currentTarget.currentTime;
    if (newTime <= playDuration) {
      setCurTimeSec(newTime);
    } else {
      setIsPlaying(false);
    }
  };

  // Can play if there's audio OR if transcription is available for read-along mode
  const playable = selectedMusicConfig || selectedVoiceConfig || (showTranscript && transcriptAvailable);
  return (
    <div className="min-h-screen relative overflow-hidden cap-player">
      <button
        onClick={() => onBack ? onBack() : navigate(-1)}
        className="absolute top-8 left-8 z-50 text-white hover:text-white/80 transition-colors flex items-center gap-2 cap-top-abs"
      >
        <GoBackIcon />
      </button>

      {/* Background image with blur */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: contentData.content.image_url ? `url(${normalizeImageUrl(contentData.content.image_url)})` : 'none',
          filter: 'blur(20px)',
          transform: 'scale(1.2)',
        }}
      />

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/40" />

      <div className="p-6 pb-36 bg-black/20 min-h-screen flex flex-col items-center justify-center z-10 relative left: 50%">
        <div className={cn("w-full mx-auto space-y-4 z-10", fullscreenContent ? "max-w-4xl" : "max-w-lg")}>
          {/* Image */}
          {!(hideArtwork && !selectionMode) && (
            <div className="aspect-square w-64 h-64 mx-auto rounded-lg overflow-hidden">
              <img
                src={normalizeImageUrl(contentData.content.image_url) || '/images/logotrans.svg'}
                alt={`${contentData.content.title} (geen bericht)`}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={retryImageOnce}
              />
            </div>
          )}

          {/* Title and Description */}
          {hideArtwork && !selectionMode ? (
            <h1 className="text-2xl font-bold text-white text-center">
              {contentData.content.title}
            </h1>
          ) : !(hideArtwork && !selectionMode) ? (
            <div className="text-center space-y-4">
              <h1 className="text-2xl font-bold text-white">
                {contentData.content.title}
              </h1>
              <p className="text-white/80">{contentData.content.description}</p>
            </div>
          ) : null}

          {/* select desired play config */}
          {selectionMode && (
            <>
              {selectionContent}

              {availableVoiceConfigs.length > 0 && (
                <div className="space-y-6">
                  <h3 className="text-white text-xl text-center">Kies stem</h3>
                  <div className="flex justify-center gap-4">
                    {availableVoiceConfigs.map((voice, voiceIdx) => (
                      <Button
                        key={voice.id}
                        // variant={voiceConfigIdx === voice.id ? "default" : "outline"}
                        onClick={() => setVoiceConfigIdx(voiceIdx)}
                        className={`${
                          voiceConfigIdx === voiceIdx
                            ? 'bg-green-500 hover:bg-green-600'
                            : 'bg-white/10 hover:bg-white/20 text-white'
                        } border-white/20 text-lg rounded-full px-8`}
                      >
                        {voice.voice_name}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* stem dropdown (disabled in favor of buttons above) */}
              {/* {availableVoiceConfigs.length > 1 && (
                <div className="flex items-center justify-between text-white bg-white/10 p-4 rounded-lg" style={{marginTop: '32px'}}>
                  <Label style={{marginRight: '18px'}}>Stem</Label>
                  <Select
                    value={voiceConfigIdx === -1 ? "geen" : String(voiceConfigIdx) || "geen"}
                    onValueChange={(value) => {
                      if (value === "geen") 
                        setVoiceConfigIdx(-1);
                      else
                        setVoiceConfigIdx(value ? Number(value) : -1);
                    }}
                    required={true}
                  >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Selecteer Stem" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="geen" key="geen-stem">Geen Stem</SelectItem>
                        {availableVoiceConfigs.map((c, idx)  => 
                          <SelectItem value={String(idx)} key={c.id}>{c.voice_name}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                </div>
              )} */}

              {/* Voice Toggle */}
              <div className="flex items-center justify-between text-white bg-white/10 p-4 rounded-lg">
                <span>Stem</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white/60">Uit</span>
                  <Switch
                    checked={voiceAudioEnabled}
                    onCheckedChange={setVoiceAudioEnabled}
                    className={`${voiceAudioEnabled ? 'bg-green-500' : ''} data-[state=checked]:bg-green-500`}
                    disabled={availableVoiceConfigs.length === 0}
                  />
                  <span className="text-sm text-white/60">Aan</span>
                </div>
              </div>

              {/* Music Toggle */}
              {availableMusicConfigs.length > 0 && (
                <div className="flex items-center justify-between text-white bg-white/10 p-4 rounded-lg">
                  <span>Muziek</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white/60">Uit</span>
                    <Switch
                      checked={musicEnabled}
                      onCheckedChange={(val) =>
                        val ? setMusicConfigIdx(0) : setMusicConfigIdx(-1)
                      }
                      className={`${musicEnabled ? 'bg-green-500' : ''} data-[state=checked]:bg-green-500`}
                    />
                    <span className="text-sm text-white/60">Aan</span>
                  </div>
                </div>
              )}

              {/* transcript toggle */}
              {(transcriptAvailable ||
                transcriptionLoading ||
                transcriptionUrl) && (
                <div className="flex items-center justify-between text-white bg-white/10 p-4 rounded-lg">
                  <span>
                    Transcriptie
                    {transcriptionLoading && (
                      <span className="text-white/60 text-sm ml-2">
                        (laden...)
                      </span>
                    )}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white/60">Uit</span>
                    <Switch
                      checked={showTranscript}
                      onCheckedChange={setShowTranscript}
                      className={`${showTranscript ? 'bg-green-500' : ''} data-[state=checked]:bg-green-500`}
                      disabled={transcriptionLoading || !transcriptAvailable}
                    />
                    <span className="text-sm text-white/60">Aan</span>
                  </div>
                </div>
              )}

              {/* Start Button */}
              <Button
                className="w-full bg-green-500 hover:bg-green-600 text-white py-6 text-lg"
                disabled={!playable}
                onClick={() => {
                  setSelectionMode(false);
                  setIsPlaying(true);
                  // Play directly in click handler for mobile compatibility
                  if (musicEnabled && musicAudioRef.current) {
                    musicAudioRef.current.play().catch(() => {});
                  }
                  if (voiceEnabled && voiceAudioRef.current) {
                    voiceAudioRef.current.play().catch(() => {});
                  }
                }}
              >
                {playable
                  ? 'Afspelen'
                  : `(selecteer muziek` +
                    (availableVoiceConfigs.length > 0 ? ' of een stem' : '') +
                    ')'}
              </Button>
            </>
          )}

          {/* Audio elements rendered outside selection mode for mobile preloading */}
          {selectedMusicConfig && (
            <audio
              ref={musicAudioRef}
              src={musicSrc}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={(e) =>
                setPlayDuration(e.currentTarget.duration)
              }
              preload="auto"
              playsInline
            />
          )}
          {selectedVoiceConfig && (
            <audio
              ref={voiceAudioRef}
              src={voiceSrc}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={(e) =>
                setPlayDuration(e.currentTarget.duration)
              }
              preload="auto"
              playsInline
            />
          )}

          {!selectionMode && playable && (
            <>
              <div style={{ marginTop: '24px' }}>
                <FavoriteButton contentId={contentData.content.id} />
                <OfflineDownloadButton
                  urls={[
                    selectedVoiceConfig?.audio_link,
                    selectedMusicConfig?.audio_link,
                  ]}
                  onChange={() => setOfflineKey((k) => k + 1)}
                />

                {fullscreenContent && !isFullscreen && (
                  <div className="mt-4">{fullscreenContent}</div>
                )}

                {showTranscript && !isFullscreen && (
                  <div
                    ref={transcriptionRef}
                    className="mt-4 h-48 rounded-2xl border border-green-600 bg-green-500/90 overflow-hidden w-full relative"
                  >
                    <div
                      ref={lyricsTrackRef}
                      className="absolute w-full text-center px-4"
                      style={{
                        transition: 'transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)',
                      }}
                    >
                      {parsedTranscription.map((line, lIdx) => {
                        const hasWordTiming = line.words && line.words.length > 0;

                        return (
                          <p
                            key={line.index}
                            id={`lyric-line-${lIdx}`}
                            className="lyric-line text-2xl font-bold my-3 opacity-20 transition-all duration-400"
                            style={{
                              lineHeight: 1.3,
                            }}
                          >
                            {hasWordTiming ? (
                              line.words.map((word, wIdx) => (
                                <span
                                  key={wIdx}
                                  id={`word-${lIdx}-${wIdx}`}
                                  className="lyric-word inline-block mx-0.5 text-white/25 transition-all duration-100"
                                >
                                  {word.text}
                                </span>
                              ))
                            ) : (
                              <span className="lyric-word text-white/25">
                                {line.text}
                              </span>
                            )}
                          </p>
                        );
                      })}
                    </div>
                  </div>
                )}
                <style>{`
                  .lyric-line.active {
                    opacity: 1 !important;
                    transform: scale(1.05);
                  }
                  .lyric-word.highlighted {
                    color: #ffffff !important;
                    text-shadow: 0 0 15px rgba(255, 255, 255, 0.7);
                  }
                `}</style>
              </div>

              {/* Fullscreen view via portal */}
              {isFullscreen && createPortal(
                <div className="fixed inset-0 z-[100] flex items-center bg-black text-white">
                  {/* Blurred background image */}
                  <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                      backgroundImage: contentData.content.image_url ? `url(${normalizeImageUrl(contentData.content.image_url)})` : 'none',
                      filter: 'blur(20px)',
                      transform: 'scale(1.2)',
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/40" />

                  {/* Close button */}
                  <button
                    onClick={() => setIsFullscreen(false)}
                    className="absolute top-8 left-8 z-50 text-white hover:text-white/80 transition-colors cap-top-abs"
                  >
                    <X className="h-8 w-8" />
                  </button>

                  {/* Favorite button */}
                  <button
                    onClick={() => toggleFavorite(contentData.content.id)}
                    className="absolute top-8 right-8 z-50 text-white hover:text-white/80 transition-colors cap-top-abs"
                  >
                    <Heart
                      className={cn(
                        'h-8 w-8',
                        favorites[contentData.content.id]
                          ? 'fill-red-500 stroke-red-500'
                          : 'stroke-white',
                      )}
                    />
                  </button>

                  <div className="w-full h-full flex flex-col items-center relative z-10 px-4 overflow-y-auto">
                    <div className="flex flex-col items-center justify-center space-y-8 w-full max-w-4xl min-h-full py-20 cap-fs-bottom">
                      {!hideArtwork && (
                        <div className="w-full max-w-[360px]">
                          <img
                            src={normalizeImageUrl(contentData.content.image_url) || '/images/logotrans.svg'}
                            alt={contentData.content.title}
                            className="w-full aspect-square rounded-lg object-cover shadow-2xl"
                            loading="lazy"
                            onError={retryImageOnce}
                          />
                        </div>
                      )}

                      {fullscreenContent}

                      {/* Transcription in fullscreen */}
                      {showTranscript && (
                        <div
                          ref={fsTranscriptionRef}
                          className="h-48 rounded-2xl border border-green-600 bg-green-500/90 overflow-hidden w-full max-w-[900px] relative"
                        >
                          <div
                            ref={fsLyricsTrackRef}
                            className="absolute w-full text-center px-4"
                            style={{
                              transition: 'transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)',
                            }}
                          >
                            {parsedTranscription.map((line, lIdx) => {
                              const hasWordTiming = line.words && line.words.length > 0;
                              return (
                                <p
                                  key={line.index}
                                  id={`lyric-line-${lIdx}`}
                                  className="lyric-line text-2xl font-bold my-3 opacity-20 transition-all duration-400"
                                  style={{ lineHeight: 1.3 }}
                                >
                                  {hasWordTiming ? (
                                    line.words.map((word, wIdx) => (
                                      <span
                                        key={wIdx}
                                        id={`word-${lIdx}-${wIdx}`}
                                        className="lyric-word inline-block mx-0.5 text-white/25 transition-all duration-100"
                                      >
                                        {word.text}
                                      </span>
                                    ))
                                  ) : (
                                    <span className="lyric-word text-white/25">
                                      {line.text}
                                    </span>
                                  )}
                                </p>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <div className="w-full max-w-[900px] bg-black/40 backdrop-blur-sm p-4 sm:p-6 rounded-lg border border-white/10">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-medium">{contentData.content.title}</h3>
                            <p className="text-sm text-muted-foreground">{contentData.content.description}</p>
                          </div>
                          <button
                            onClick={() => setIsFullscreen(false)}
                            className="p-2 hover:bg-white/10 rounded-full"
                          >
                            <Minimize2 className="h-4 w-4" />
                          </button>
                        </div>

                        <div className="space-y-4">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-white/60">
                              {formatTime(curTimeSec)}
                            </span>
                            <div
                              className="flex-1 h-1 bg-gray-600 rounded-full cursor-pointer"
                              onClick={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const clickPosition = e.clientX - rect.left;
                                const percentageClicked = Math.min(
                                  Math.max(clickPosition / rect.width, 0),
                                  1,
                                );
                                const newTime = percentageClicked * playDuration;
                                if (musicAudioRef.current) musicAudioRef.current.currentTime = newTime;
                                if (voiceAudioRef.current) voiceAudioRef.current.currentTime = newTime;
                                document.querySelectorAll('.lyric-word.highlighted').forEach((el) => el.classList.remove('highlighted'));
                                document.querySelectorAll('.lyric-line.active').forEach((el) => el.classList.remove('active'));
                              }}
                            >
                              <div
                                className="h-full bg-white rounded-full transition-all"
                                style={{ width: `${(curTimeSec / playDuration) * 100}%` }}
                              />
                            </div>
                            <span className="text-xs text-white/60">
                              {formatTime(playDuration - curTimeSec)}
                            </span>
                          </div>

                          <div className="flex items-center">
                            <div className="flex-1" />
                            <div className="flex items-center gap-8">
                              <button
                                className="p-2 hover:bg-white/10 rounded-full text-white/60 hover:text-white"
                                onClick={() => {
                                  if (musicAudioRef.current) musicAudioRef.current.currentTime = 0;
                                  if (voiceAudioRef.current) voiceAudioRef.current.currentTime = 0;
                                  setCurTimeSec(0);
                                  simulatedTimeRef.current = 0;
                                }}
                              >
                                <SkipBack className="h-6 w-6" fill="currentColor" />
                              </button>

                              <button
                                className="p-3 bg-white rounded-full hover:bg-white/90 transition-colors"
                                onClick={() => {
                                  if (isPlaying) {
                                    if (musicAudioRef.current) musicAudioRef.current.pause();
                                    if (voiceAudioRef.current) voiceAudioRef.current.pause();
                                    setIsPlaying(false);
                                  } else {
                                    const promises: Promise<void>[] = [];
                                    if (musicEnabled && musicAudioRef.current) promises.push(musicAudioRef.current.play());
                                    if (voiceEnabled && voiceAudioRef.current) promises.push(voiceAudioRef.current.play());
                                    Promise.all(promises)
                                      .then(() => setIsPlaying(true))
                                      .catch((err) => console.log('Playback prevented:', err));
                                  }
                                }}
                              >
                                {isPlaying ? (
                                  <Pause className="h-6 w-6 text-black" fill="black" />
                                ) : (
                                  <Play className="h-6 w-6 text-black" fill="black" />
                                )}
                              </button>

                              {onNext && (
                                <button
                                  className="p-2 hover:bg-white/10 rounded-full text-white/60 hover:text-white"
                                  onClick={onNext}
                                >
                                  <SkipForward className="h-6 w-6" fill="currentColor" />
                                </button>
                              )}
                            </div>

                            {/* The spacer stays on iOS, where the slider is hidden, so the controls stay centred */}
                            <div className="flex-1 flex items-center justify-end gap-2">
                            {!isIOS && (<>
                              <Volume2 className="h-6 w-6 text-white/40" />
                              <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.01"
                                value={volume}
                                onChange={(e) => {
                                  const v = parseFloat(e.target.value);
                                  setVolume(v);
                                  if (musicAudioRef.current) musicAudioRef.current.volume = v;
                                  if (voiceAudioRef.current) voiceAudioRef.current.volume = v;
                                }}
                                className="w-24 h-1 accent-white cursor-pointer"
                              />
                            </>)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>,
                document.body,
              )}

              {/* Fixed bottom player bar */}
              {!isFullscreen && (
              <div className="fixed bottom-0 left-0 right-0 lg:left-64 z-50">
                <div className="flex items-center space-x-4 bg-black/40 backdrop-blur-sm p-4 rounded-none sm:rounded-lg border border-white/10 text-white transition-all duration-300 cap-bottom">
                  <div className="relative z-10 hidden sm:block">
                    <img
                      src={normalizeImageUrl(contentData.content.image_url) || '/images/logotrans.svg'}
                      alt={contentData.content.title}
                      className="h-24 w-24 rounded-md object-cover"
                      loading="lazy"
                      onError={retryImageOnce}
                    />
                  </div>
                  <div className="flex-1 relative z-10">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h3 className="text-sm font-medium">{contentData.content.title}</h3>
                      </div>
                      <button
                        onClick={() => setIsFullscreen(true)}
                        className="p-2 hover:bg-white/10 rounded-full ml-4 hidden lg:block"
                      >
                        <Maximize2 className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-white/60">
                          {formatTime(curTimeSec)}
                        </span>
                        <div
                          className="flex-1 h-1 bg-gray-600 rounded-full cursor-pointer"
                          onClick={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const clickPosition = e.clientX - rect.left;
                            const percentageClicked = Math.min(
                              Math.max(clickPosition / rect.width, 0),
                              1,
                            );
                            const newTime = percentageClicked * playDuration;

                            if (musicAudioRef.current) {
                              musicAudioRef.current.currentTime = newTime;
                            }
                            if (voiceAudioRef.current) {
                              voiceAudioRef.current.currentTime = newTime;
                            }

                            document.querySelectorAll('.lyric-word.highlighted').forEach((el) => {
                              el.classList.remove('highlighted');
                            });
                            document.querySelectorAll('.lyric-line.active').forEach((el) => {
                              el.classList.remove('active');
                            });
                          }}
                        >
                          <div
                            className="h-full bg-white rounded-full"
                            style={{
                              width: `${(curTimeSec / playDuration) * 100}%`,
                            }}
                          />
                        </div>
                        <span className="text-xs text-white/60">
                          {formatTime(playDuration - curTimeSec)}
                        </span>
                      </div>

                      <div className="flex items-center">
                        <div className="flex-1" />
                        <div className="flex items-center gap-6">
                          <button
                            className="p-1.5 hover:bg-white/10 rounded-full text-white/60 hover:text-white transition-colors"
                            onClick={() => {
                              if (musicAudioRef.current) musicAudioRef.current.currentTime = 0;
                              if (voiceAudioRef.current) voiceAudioRef.current.currentTime = 0;
                              setCurTimeSec(0);
                              simulatedTimeRef.current = 0;
                            }}
                          >
                            <SkipBack className="h-6 w-6" fill="currentColor" />
                          </button>

                          <button
                            className="p-2 bg-white rounded-full hover:bg-white/90 transition-colors"
                            onClick={() => {
                              if (isPlaying) {
                                if (musicAudioRef.current) musicAudioRef.current.pause();
                                if (voiceAudioRef.current) voiceAudioRef.current.pause();
                                setIsPlaying(false);
                              } else {
                                const promises: Promise<void>[] = [];
                                if (musicEnabled && musicAudioRef.current) promises.push(musicAudioRef.current.play());
                                if (voiceEnabled && voiceAudioRef.current) promises.push(voiceAudioRef.current.play());
                                Promise.all(promises)
                                  .then(() => setIsPlaying(true))
                                  .catch((err) => console.log('Playback prevented:', err));
                              }
                            }}
                          >
                            {isPlaying ? (
                              <Pause className="h-5 w-5 text-black" fill="black" />
                            ) : (
                              <Play className="h-5 w-5 text-black" fill="black" />
                            )}
                          </button>

                          {onNext && (
                            <button
                              className="p-1.5 hover:bg-white/10 rounded-full text-white/60 hover:text-white transition-colors"
                              onClick={onNext}
                            >
                              <SkipForward className="h-6 w-6" fill="currentColor" />
                            </button>
                          )}
                        </div>

                        <div className="flex-1 flex items-center justify-end gap-2">
                          {/* Mobile volume button with vertical popup */}
                          <div ref={mobileVolumeRef} className={`relative sm:hidden${isIOS ? ' hidden' : ''}`}>
                            <button
                              className="p-1.5 hover:bg-white/10 rounded-full text-white/60 hover:text-white transition-colors"
                              onClick={() => setShowMobileVolume(!showMobileVolume)}
                            >
                              <Volume2 className="h-6 w-6" />
                            </button>
                            {showMobileVolume && (
                              <div className="absolute bottom-full right-0 mb-2 z-50 bg-black/80 backdrop-blur-sm rounded-lg p-3 border border-white/10 flex flex-col items-center gap-2">
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.01"
                                  value={volume}
                                  onChange={(e) => {
                                    const v = parseFloat(e.target.value);
                                    setVolume(v);
                                    if (musicAudioRef.current) musicAudioRef.current.volume = v;
                                    if (voiceAudioRef.current) voiceAudioRef.current.volume = v;
                                  }}
                                  className="h-24 accent-white cursor-pointer"
                                  style={{ writingMode: 'vertical-lr', direction: 'rtl' }}
                                />
                                <Volume2 className="h-4 w-4 text-white/40" />
                              </div>
                            )}
                          </div>
                          {/* Desktop volume slider */}
                          <div className={`hidden ${isIOS ? '' : 'sm:flex'} items-center gap-2`}>
                            <Volume2 className="h-6 w-6 text-white/40" />
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.01"
                              value={volume}
                              onChange={(e) => {
                                const v = parseFloat(e.target.value);
                                setVolume(v);
                                if (musicAudioRef.current) musicAudioRef.current.volume = v;
                                if (voiceAudioRef.current) voiceAudioRef.current.volume = v;
                              }}
                              className="w-20 h-1 accent-white cursor-pointer"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
